import {
  Button,
  Comments,
  CompanyName,
  DropDown,
  Email,
  FirstName,
  JobTitle,
  LastName,
  MarketingConsentKinto,
  Tel,
  VehicleRegNo,
} from '@components/form/fields'
import { Form, FormStep, FormWrapper } from '@components/form/layout'
import { Nav } from '@components/ui'
import { yupResolver } from '@hookform/resolvers/yup'
import { ERROR_MESSAGE } from '@utils/constants'
import closeOverlay from '@utils/helpers/closeOverlay'
import { getContentFile, getPageData } from '@utils/helpers/index-server'
import {
  customEvent,
  formEvent,
  updateDatalayer,
} from '@utils/helpers/tracking'
import { KintoContactUsSchema } from '@utils/schemas'
import axios from 'axios'
import type { GetServerSideProps } from 'next'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

const KintoContactUs = ({ content, tracking, query, brand }) => {
  const [submitResponse, setSubmitResponse] = useState('')
  const [isNewCustomer, setisNewCustomer] = useState(
    query.enquiry === 'new-customer',
  )

  const enquiryTypes = [
    {
      id: 'new-customer',
      label: 'New Customer',
      value: 'New Customer',
    },
    {
      id: 'existing-customer',
      label: 'Existing Customer',
      value: 'Existing Customer',
    },
    {
      id: 'kinto-one',
      label: 'KINTO One',
      value: 'KINTO One',
      hidden: true,
    },
  ]

  const fleetSize = [
    {
      id: 'driver',
      label: 'Driver',
      value: 'Driver',
    },
    {
      id: 'under-100',
      label: 'SME fleet under 100',
      value: 'SME fleet under 100',
    },
    {
      id: '100-1000',
      label: 'Corporate or public sector fleet 100-1000',
      value: 'Corporate or public sector fleet 100-1000',
    },
    {
      id: 'over-1000',
      label: 'Corporate or public sector fleet over 1000',
      value: 'Corporate or public sector fleet over 1000',
    },
  ]

  const initialValues = {
    type: 'CU',
    enquiry: enquiryTypes.find((e) => e.id === query.enquiry)?.value || '',
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    tel: '',
    reg_num: '',
    job_title: '',
    fleet_size: '',
    notes: '',
    consent: false,
  }

  const methods = useForm({
    resolver: yupResolver(KintoContactUsSchema),
    defaultValues: initialValues,
    mode: 'onTouched',
  })

  const variantMapping = (data) =>
    enquiryTypes.find((e) => e.value === data.enquiry)?.id ||
    query.enquiry ||
    ''

  useEffect(() => {
    if (methods.formState.isDirty) {
      updateDatalayer('service', {
        brand: 'kinto',
        service: tracking.variant === 'kinto-one' ? 'one' : 'general',
      })
      customEvent({
        name: 'new-formevent',
        componentName: tracking.form,
        componentGroup: 'services',
        action: 'form-fill-start',
        label: `contact-request-${
          tracking.variant === 'kinto-one' ? 'one' : 'general'
        }`,
        variant:
          tracking.variant ||
          variantMapping({ enquiry: methods.getValues('enquiry') }) ||
          '',
      })
    }
  }, [methods.formState.isDirty])

  useEffect(() => {
    if (methods.getValues('enquiry') === 'New Customer') {
      setisNewCustomer(true)
    }
  }, [methods.getValues('enquiry')])

  const onSubmit = async (data) => {
    await axios({
      method: 'post',
      url: `${process.env.NEXT_PUBLIC_API}/kinto`,
      headers: {
        'Content-Type': 'application/json',
      },
      data,
    })
      .then(() => {
        formEvent(
          {
            conversion: 1,
            action: 'form-success',
            name: tracking.form,
            step: `contact-request-${
              tracking.variant === 'kinto-one' ? 'one' : 'general'
            }`,
            variant: variantMapping(data),
            page: tracking.page,
          },
          brand,
          data.email,
        )
        closeOverlay(
          content.confirmation.title,
          content.confirmation.copy,
          setSubmitResponse,
          true,
        )
      })
      .catch(() => {
        formEvent(
          {
            conversion: 0,
            name: tracking.form,
            step: 'form-error',
            variant: variantMapping(data),
            page: tracking.page,
          },
          brand,
        )
        setSubmitResponse(ERROR_MESSAGE)
        throw new Error(ERROR_MESSAGE)
      })
  }

  const visibleEnquiryTypes = enquiryTypes.filter((e) => !e.hidden)

  return (
    <>
      <Nav
        title={content.title}
        logoType={content?.logotype}
        customLogo={content?.customlogo}
      />
      <FormWrapper>
        <FormStep
          title={content.details.title}
          subtitle={content.details.subtitle}
          step={1}
          active={true}
        >
          <FormProvider {...methods}>
            <Form onSubmit={methods.handleSubmit(onSubmit)}>
              {initialValues.enquiry === '' && (
                <DropDown
                  name="enquiry"
                  label="Type of enquiry"
                  options={visibleEnquiryTypes}
                  onChange={(e) => {
                    methods.setValue(
                      'enquiry',
                      visibleEnquiryTypes[e.target.selectedIndex - 1].value,
                      {
                        shouldDirty: true,
                      },
                    )
                    setisNewCustomer(e.target.value === 'New Customer')
                    if (e.target.value === 'New Customer') {
                      methods.resetField('job_title')
                      methods.resetField('fleet_size')
                    }
                  }}
                />
              )}
              <FirstName />
              <LastName />
              <CompanyName optional={isNewCustomer} />
              <Email />
              <Tel />
              {!isNewCustomer && <VehicleRegNo optional />}
              {isNewCustomer && (
                <div>
                  <JobTitle optional />
                  <DropDown
                    name="fleet_size"
                    label="Fleet size"
                    options={fleetSize}
                  />
                </div>
              )}
              <Comments
                name="notes"
                label="How can we help?"
                characterLimit={2000}
              />
              <MarketingConsentKinto />
              <Button submitResponse={submitResponse}>
                {content.details.submit}
              </Button>
            </Form>
          </FormProvider>
        </FormStep>
      </FormWrapper>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const brand = context.locale
  const form = 'contact-us'
  const [content, settings] = getContentFile(brand, `forms/${form}`)

  // redirect if not active and redirect url exist
  if (!content.attributes?.active && content.attributes?.redirect) {
    const destination = new URL(content.attributes?.redirect)

    Object.entries(context.query).forEach((element) => {
      if (element.length > 1 && element[0] !== 'id') {
        const key = element[0]
        const value = element[1] as string
        destination.searchParams.append(key, value)
      }
    })

    return {
      redirect: {
        destination: destination.href,
        permanent: true,
      },
    }
  }

  // if no content/disabled - show 404
  if (!content || !content.attributes?.active) {
    return {
      notFound: true,
    }
  }

  // if content property 'hold' or settings property 'maintenance' is true, redirect to holding page
  if (content.attributes?.hold || settings.attributes.maintenance) {
    return {
      redirect: {
        destination: '/maintenance',
        permanent: false,
      },
    }
  }

  return {
    props: {
      content: content.attributes,
      title: content.attributes.title,
      tracking: {
        page: content.attributes.tracking?.page,
        form: content.attributes.tracking?.form,
        variant: context.query?.enquiry || '',
        initialStep: 'form-fill-start',
      },
      description: '',
      ...(await getPageData(context)),
    },
  }
}

export default KintoContactUs
